/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  FirebaseAuthentication,
  SignInWithOAuthOptions,
  User,
} from "@capacitor-firebase/authentication";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
} from "amazon-cognito-identity-js";
import { AuthServiceProvider } from "auth";
import { AxiosError, type AxiosInstance } from "axios";
import createAuthRefreshInterceptor from "axios-auth-refresh";
import { FirebaseOptions, getApps, initializeApp } from "firebase/app";
import type { History } from "history";
import Cookies from "js-cookie";
import { AuthModel } from "main";
import { getErrorMessage } from "swing-utils";

/**
 *
 * The general auth flow is as follows:
 *  1) upon initial "sign in:
 *     - authenticate with the provider (Google or Cognito)
 *     - store response in singletonStorage (localStorage) and set in-memory app data
 *     - query Swing API for user data sending along the `idToken` in Authorization header
 *     - store response in-memory via "AuthModel" class
 *  2) for subsequent requests:
 *     - should a 401 or 403 error occur then the middleware intercepts request(s) and:
 *        - attempts to refresh the `idToken`:
 *          - if successful then singletonStorage (localStorage) updated, in-memory app data
 *            updated and middleware will perform the failed API request(s) again
 *          - if failure then user is signed out
 *     - should the refresh token itself be expired then user is signed out
 *  3) upon browser app refresh/reload:
 *     - we check singletonStorage (localStorage) for authentication data and:
 *        - if it exists then we use that data to update in-memory app data and perform any
 *          Swing API requests passing along the `idToken` in the Authorization header
 *        - if it doesn't exist or it isn't accessible then the user is signed out
 *
 */

/**
 * Auth Context Set Up
 */
type AuthContextState<TUser> = {
  /**
   * Represents the status of the user's authentication state.
   * - `undefined` signifies that we do not know if the user is authenticated or not.
   * - `AUTHENTICATED` signifies that the user is authenticated.
   * - `UN_AUTHENTICATED` signifies that the user is not authenticated.
   */
  status?: "AUTHENTICATED" | "UN_AUTHENTICATED";
  /**
   * Whether the user is currently authenticating.
   * Can be used to indicate an in-flight authentication process.
   *
   * @default false
   */
  isAuthenticating: boolean;
  /**
   * The auth data captured from provider login.
   * Using `{}` so callers do not need to handle whether it is null or not.
   *
   * @default {}
   */
  authInfo: AuthModel;
  /**
   * Whether the user data is currently being fetched.
   * Can be used to indicate an in-flight call to get user.
   *
   * @default false
   */
  isFetchingUser: boolean;
  /**
   * The Swing user data.
   * Using `{}` so callers do not need to handle whether it is null or not.
   *
   * @default {}
   */
  userInfo: TUser | Record<string, never>;
  /**
   * Swing API sub error.
   *
   * @default undefined
   */
  apiError?: AxiosError | undefined;
};

// Note: improves caller type safety, however we are not
// able to use 'AuthServiceProvider' union type below
// (enum would allow this but not worth the refactor effort at this time)
// export type SignInActionProps =
//   | {
//       provider: "google";
//     }
//   | {
//       provider: "cognito";
//       user: {
//         email: string;
//         password: string;
//       };
//     };
export type SignInActionProps = {
  provider: AuthServiceProvider;
  user?: {
    email: string;
    password: string;
  };
};

/**
 * [Optional] Arguments to allow this function to perform the sign out redirect
 * which defaults to `/` but can be overridden via `redirectPath`.
 *
 * TODO: Check if the typing comes out nicely with this function.
 *
 * @example
 * // Will not redirect after logging out, the calling application will need to
 * // handle the redirect.
 * signOut();
 *
 * @example
 * // Will redirect to `/` after logging out.
 * const history = useHistory(); // From `react-router`
 * signOut({ history });
 *
 * @example
 * // Will redirect to `/login` after logging out.
 * const history = useHistory(); // From `react-router`
 * signOut({ history, redirectPath: "/login" });
 */
type SignOutActionParams = {
  /** The `useHistory` reference to perform a redirect after logging out. */
  history?: History;
  /**
   * [Optional] Path to push after logging out
   * @default "/"
   */
  redirectPath?: string;
  apiError?: AxiosError;
};

type AuthContextActions = {
  // passing provider allows the FE a way to determine how the user is attempting to sign in
  signIn: (props: SignInActionProps) => Promise<void>;
  /**
   * Sign out helper which can perform a redirect if given a `history` object.
   *
   * @example
   * // Will not redirect after logging out, the calling application will need to
   * // handle the redirect.
   * signOut();
   *
   * @example
   * // Will redirect to `/` after logging out.
   * const history = useHistory(); // From `react-router`
   * signOut({ history });
   *
   * @example
   * // Will redirect to `/login` after logging out.
   * const history = useHistory(); // From `react-router`
   * signOut({ history, redirectPath: "/login" });
   */
  signOut: (params?: SignOutActionParams) => Promise<void>;
  /**
   * Re-fetches user data refreshing the `userInfo` state.
   * Use case:
   * - Refresh the user data while a Sub is onboarding to capture any changes
   *   in their `onboardingStatus` which would trigger a redirect to another
   *   page.
   * */
  refreshUser: () => Promise<void>;
  forgotPasswordRequest: (email: string) => Promise<unknown>;
  forgotPasswordSubmit: (email: string, code: string, newPassword: string) => Promise<unknown>;
};

type AuthContextValue<TUser = any> = AuthContextState<TUser> & AuthContextActions;

const AuthContext = createContext<AuthContextValue | null>(null);

// AUTH PROVIDER SET UP

export type AuthProviderProps = {
  api:
    | AxiosInstance
    | {
        defaults: {
          headers: { common: { Authorization: ""; "authenticate-with-legacy-token"?: boolean } };
        };
      }; // empty object for testing / storybook
  appAuthCookieName: string;
  authCookieDomain: string;
  children: ReactNode;
  firebaseConfig: FirebaseOptions;
  isTest?: boolean; // avoid instantiating provider auth in tests
  isWeb?: boolean;
  legacyAuthCookieName: string;
  queryClient: any;
  storage: any;
  storageAuthKey: string;
  userApi: () => Promise<any>;
  userPool: CognitoUserPool;
  userSyncApi: () => Promise<any>;
  vgsApi?:
    | AxiosInstance
    | {
        defaults: {
          headers: { common: { Authorization: "" } };
        };
      }; // empty object for testing / storybook
};

// data structure of the legacy auth cookie set by `suber.web` which is used to authenticate an
// admin or sub user into Swing Schools or Swing Subs app if they logged into the legacy app first
interface LegacyAuthData {
  "google/token": string;
  "google/given-name": string;
  "google/family-name": string;
  "google/email": string;
  "google/id": string;
  "google/image": string;
  "cognito/token": string;
  "cognito/first-name": string;
  ":cognito/last-name": string;
  ":cognito/id": string;
  ":cognito/email": string;
}

const initialState: AuthContextState<any> = {
  status: undefined, // This is not required, but setting to show that it defaults to `undefined`
  isAuthenticating: false,
  isFetchingUser: false,
  authInfo: {} as AuthModel,
  userInfo: {},
  apiError: undefined,
};

export function AuthProvider(props: AuthProviderProps) {
  const {
    api,
    appAuthCookieName,
    authCookieDomain,
    children,
    firebaseConfig,
    isTest = false,
    isWeb = false,
    legacyAuthCookieName,
    queryClient,
    storage,
    storageAuthKey,
    userApi,
    userPool,
    userSyncApi,
    vgsApi,
  } = props;

  // throw if more than 1 attempted
  const authContext = useContext(AuthContext);
  if (authContext) throw new Error("AuthProvider must only be used once!");

  // internal AuthProvider state
  const [state, setState] = useState(initialState);

  // auth cookie
  const legacyAuthCookie = Cookies.get(legacyAuthCookieName);
  const authCookieOptions: { domain: string; expires: number; sameSite: "none"; secure: boolean } =
    useMemo(
      () => ({
        domain: authCookieDomain,
        expires: 30,
        sameSite: "none",
        secure: true,
      }),
      [authCookieDomain],
    );

  /**
   * FIREBASE (GOOGLE) INITIALIZATION
   *  - web only initialization of Google Auth
   *  - capacitor plugin uses a different process for native mobile apps
   */
  useEffect(() => {
    const initialize = async () => {
      if (isWeb && !isTest) {
        // check if apps already initialized to avoid conflict
        if (!getApps().length) {
          initializeApp(firebaseConfig);
        }
      }
    };
    initialize();
  }, [firebaseConfig, isTest, isWeb]);

  /**
   * SYNC FIREBASE CURRENT USER
   *  - sets the current user correctly upon app refresh / reload
   *  - without this `.getCurrentUser` can return `null` causing `.getIdToken` to fail
   */
  useEffect(() => {
    async function getFirebaseUser() {
      if (!isTest) {
        // @param currentUser: User | null
        await FirebaseAuthentication.addListener("authStateChange", async () => {
          // we are using this to trigger the internal Firebase state to ensure
          // `.getCurrentUser` returns a `User` when expected and not `null`
          // note: not currently using the `currentUser` param
        });
      }
    }

    async function cleanup() {
      if (!isTest) {
        await FirebaseAuthentication.removeAllListeners();
      }
    }

    getFirebaseUser();

    return () => {
      cleanup();
    };
  }, [isTest]);

  /**
   * FIREBASE GET CURRENT USER
   *  - attempt to retrieve data from Firebase for the
   *    currently authenticated user
   */
  const getFirebaseCurrentUser = async () => {
    try {
      const currentUser = await FirebaseAuthentication.getCurrentUser();
      return currentUser.user;
    } catch (error) {
      console.error("Firebase current user - error: ", error);
    }
  };

  /**
   * FIREBASE (GOOGLE) SIGN IN
   *  - use capacitor plugin to trigger the Google sign in prompt UI
   */
  const onGoogleSignIn = useCallback(async (options?: SignInWithOAuthOptions) => {
    try {
      const result = await FirebaseAuthentication.signInWithGoogle(options);
      return result.user;
    } catch (err) {
      console.error("onGoogleSignIn - error: ", err);
      // ignore error if pop up is closed by user
      if (!getErrorMessage(err).includes("popup-closed-by-user")) {
        console.error(getErrorMessage(err));
      }
    }
  }, []);

  /**
   * COGNITO SIGN IN
   */
  const onCognitoSignIn = useCallback(
    async (cognitoUser: { email: string; password: string }): Promise<CognitoUserSession> => {
      const user = new CognitoUser({
        Username: cognitoUser.email,
        Pool: userPool,
        Storage: storage,
      });
      const authDetails = new AuthenticationDetails({
        Username: cognitoUser.email,
        Password: cognitoUser.password,
      });
      return new Promise((resolve, reject) => {
        // Note: when in test we need to use this resolve fn. However, if using
        // a mock or spy then we don't have access to this resolve fn, therefore
        // instead of returning we'll set it to a variable so we can have access
        const userResult = user.authenticateUser(authDetails, {
          onSuccess: (userSession) => {
            // accessToken, idToken and refreshToken
            return resolve(userSession);
          },
          onFailure: (err) => {
            // { message: "User does not exist." } // username incorrect
            // { message: "Incorrect username or password." } // username correct, password incorrect
            // eslint-disable-next-line no-console
            console.error("authenticateUser - error: ", err);
            return reject(err);
          },
        });
        // when not in test return normal behavior
        if (!isTest) {
          return userResult;
        } else {
          return (userResult as unknown as Promise<CognitoUserSession>)
            .then((userSession) => resolve(userSession))
            .catch((err) => reject(err));
        }
      });
    },
    [isTest, storage, userPool],
  );

  /**
   * FIREBASE (GOOGLE) SIGN OUT
   */
  const onGoogleSignOut = useCallback(async () => {
    return await FirebaseAuthentication.signOut();
  }, []);

  /**
   * COGNITO SIGN OUT
   */
  const onCognitoSignOut = useCallback(async () => {
    return new Promise((resolve, reject) => {
      const user = userPool.getCurrentUser();
      if (user) {
        return resolve(user.signOut());
      }
      return reject("No user found");
    });
  }, [userPool]);

  /**
   * SIGN OUT HELPER
   *  - generic function to handle state when a user signs out
   */
  const onSignOut = useCallback(
    (params?: SignOutActionParams) => {
      // remove auth data (tokens) from storage
      storage.clear();
      // remove app cookie (admin app or sub app based on implementation)
      Cookies.remove(appAuthCookieName, authCookieOptions);
      // remove legacy auth cookie (admin app or sub app based on implementation)
      Cookies.remove(legacyAuthCookieName, { path: "/", domain: authCookieDomain });
      // remove header(s)
      delete api.defaults.headers.common.Authorization;
      delete api.defaults.headers.common["authenticate-with-legacy-token"];
      if (vgsApi) {
        delete vgsApi.defaults.headers.common.Authorization;
      }
      // reset in-memory instance
      setState(() => ({
        ...initialState,
        // We can safely assert that the user is not authenticated
        status: "UN_AUTHENTICATED",
        apiError: params?.apiError ?? undefined,
      }));

      // remove queries associated to user - important to "clear" queries to
      // avoid previous queries from running once signing back in after signing out
      queryClient.removeQueries();

      // Redirect if history is provided.
      const { history, redirectPath = "/" } = params ?? {};
      if (history) {
        history.push(redirectPath);
      }
    },
    [
      api.defaults.headers.common,
      appAuthCookieName,
      authCookieDomain,
      authCookieOptions,
      legacyAuthCookieName,
      queryClient,
      storage,
      vgsApi,
    ],
  );

  /**
   * COGNITO FORGOT PASSWORD - REQUEST
   */
  const forgotPasswordRequest = useCallback(
    async (email: string) => {
      const user = new CognitoUser({
        Username: email,
        Pool: userPool,
      });
      return new Promise((resolve, reject) => {
        // request a verification code to be sent
        return user.forgotPassword({
          onSuccess: (result) => {
            /**
           * {
                "CodeDeliveryDetails": {
                  "AttributeName": "email",
                  "DeliveryMedium": "EMAIL",
                  "Destination": "d***@s***"
                }
              }
           */
            return resolve(result);
          },
          onFailure: (err) => {
            // UserNotFoundException: Username/client id combination not found.
            // eslint-disable-next-line no-console
            console.error("forgotPassword - error: ", err);
            return reject(err);
          },
          // the user can input the verification code on another page or at a later time. By
          // setting inputVerificationCode to undefined the onSuccess method is called immediately
          // assuming there are no errors allowing this function to resolve
          inputVerificationCode: undefined,
        });
      });
    },
    [userPool],
  );

  /**
   * COGNITO FORGOT PASSWORD - SUBMIT
   */
  const forgotPasswordSubmit = useCallback(
    async (email: string, code: string, newPassword: string) => {
      const user = new CognitoUser({
        Username: email,
        Pool: userPool,
      });
      return new Promise((resolve, reject) => {
        // reset password
        return user.confirmPassword(code, newPassword, {
          onSuccess: (result) => {
            return resolve(result);
          },
          onFailure: (err) => {
            /**
             * email not found === UserNotFoundException: Username/client id combination not found.
             * wrong code === CodeMismatchException: Invalid verification code provided, please try again.
             * invalid password === InvalidPasswordException: Password does not conform to policy: Password must have uppercase characters
             */
            console.error("confirmPassword - error: ", err);
            return reject(err);
          },
        });
      });
    },
    [userPool],
  );

  /**
   * HANDLE AUTH REFRESH FAILURE
   *  - clears app state and forces a reload of the app redirecting
   *    the user to the root URL
   */
  const handleRefreshFailure = useCallback(() => {
    onSignOut();
    return window.location.assign("/");
  }, [onSignOut]);

  /**
   * SWING API - GET
   *  - fetch (retrieve) currently signed in user data from Swing
   */
  const { refetch: getUserData } = useQuery(
    {
      queryKey: ["userData"],
      queryFn: userApi,
      // "enabled" set to false so we don't run automatically as
      // we want to control when we call this query
      enabled: false,
      // note: explicitly do not retry for 404 (user does not exist) or 500 (user exists but incorrect user type, i.e. an un-aliased admin)
      // by using a fn we cannot use default retry behavior and must handle all cases
      retry: (failureCount: number, error: AxiosError) => {
        // no retry
        if (error.response?.status === 404 || error.response?.status === 500) {
          onSignOut({ apiError: error });
          return false;
        }
        // true = infinite retries so need to explicity return false when 3 retries have occurred (default behavior)
        if (failureCount <= 3) {
          return true;
        }
        return false;
      },
      // note: we do not throw / bubble up error (default behavior)
      // throwOnError: false
    },
    queryClient,
  );

  /**
   * SWING API - POST
   *  - sync google user data with Swing
   */
  const { mutateAsync: syncGoogleUserData } = useMutation(
    {
      mutationFn: () => userSyncApi(),
      // note: explicitly do not retry for 404 (user does not exist) or 500 (user exists but incorrect user type, i.e. an un-aliased admin)
      // by using a fn we cannot use default retry behavior and must handle all cases
      retry: (failureCount: number, error: AxiosError) => {
        // no retry
        if (error.response?.status === 404 || error.response?.status === 500) {
          onSignOut({ apiError: error });
          return false;
        }
        // true = infinite retries so need to explicity return false when 3 retires have occurred (default behavior)
        if (failureCount <= 3) {
          return true;
        }
        return false;
      },
      // note: we do not throw / bubble up error (default behavior)
      // throwOnError: false
    },
    queryClient,
  );

  /**
   * HANDLE SWING USER DATA
   *  - attempt to retrieve user data
   *  - set app data with updated user data
   */
  const fetchUser = useCallback(async (): Promise<void> => {
    try {
      /**
       * controlling this ourselves vs. using `fetchStatus` from React Query
       * as it was causing too many re-renders. Also exposing `fetchStatus`
       * directly in the `value` of our context provider causes an additional
       * re-render which we can avoid with the implementation below
       */
      setState((prevState) => ({ ...prevState, isFetchingUser: true }));

      const { data } = await getUserData();

      setState((prevState) => ({
        ...prevState,
        userInfo: data ?? {},
        status: data && Object.keys(data).length > 0 ? "AUTHENTICATED" : "UN_AUTHENTICATED",
      }));

      // Note: needs to be a separate setState otherwise `isFetchingUser`
      // will not be updated to false believed to be due to React batching
      setState((prevState) => ({ ...prevState, isFetchingUser: false }));

      return data;
    } catch (err) {
      // log error
      console.error(getErrorMessage(err));
      // if axios error then log additional error details
      if (err instanceof AxiosError) {
        console.error(err.response?.data);
      }
      // reset auth state since we cannot retrieve user
      onSignOut();
    }
  }, [getUserData, onSignOut]);

  /**
   * HANDLE PAGE REFRESH
   *  - when app is refreshed/reloaded, attempt to keep the
   *    user signed in if auth data exists
   */
  useEffect(() => {
    const fetchUserData = async () => await fetchUser();

    const parseLegacyAuthCookie = (legacyAuthCookie: string) => {
      try {
        return JSON.parse(legacyAuthCookie);
      } catch {
        return JSON.parse(atob(legacyAuthCookie));
      }
    };

    const getLegacyAuthData = () => {
      if (legacyAuthCookie) {
        try {
          const legacyAuthData = parseLegacyAuthCookie(legacyAuthCookie);
          api.defaults.headers.common["authenticate-with-legacy-token"] = true;
          return legacyAuthData;
        } catch {
          // remove legacy auth cookie (admin app or sub app based on implementation)
          Cookies.remove(legacyAuthCookieName, { path: "/", domain: authCookieDomain });
        }
      }
    };

    const legacyAuthData: LegacyAuthData = getLegacyAuthData();

    const buildAuthData = (
      authData: User | CognitoUserSession | LegacyAuthData,
      provider: string,
    ) => {
      const authExtras: { provider?: string; refreshTokenExpiration?: number } = {};
      // Note: refresh token not available via legacy cookie login
      if (provider === "cognito") {
        // for now, set expiration 1 day ahead of today's date to allow Cognito
        // to determine when refresh token has expired
        const COGNITO_REFRESH_TOKEN_TIMESTAMP = new Date(
          Date.now() + 1000 * 60 * 60 * 24 * 1,
        ).getTime();
        authExtras["refreshTokenExpiration"] = COGNITO_REFRESH_TOKEN_TIMESTAMP;
      }
      // add provider data
      authExtras["provider"] = provider;
      return { ...authData, ...authExtras };
    };

    // check if we have auth data in our storage mechanism
    const storageData =
      storage.getItem(storageAuthKey) ||
      (legacyAuthData &&
        buildAuthData(legacyAuthData, legacyAuthData["google/token"] ? "google" : "cognito"));
    if (storageData) {
      const authData = new AuthModel(storageData);
      // if available user is authenticated so set our app / in-memory state
      setState((prevState) => ({
        ...prevState,
        authInfo: authData,
      }));
      // update headers on instance
      api.defaults.headers.common.Authorization = `Token ${authData.token}`;
      // uncomment setting `ngrok-skip-browser-warning` header to test VGS proxy locally using ngrok.
      // Ngrok returns a warning page response without this header
      // vgsApi.defaults.headers.common["ngrok-skip-browser-warning"] = true;
      if (vgsApi) {
        vgsApi.defaults.headers.common.Authorization = `Token ${authData.token}`;
      }
      // call API to get user data
      fetchUserData();
    } else {
      // Otherwise, we can assert that the user is not authenticated
      setState({ ...initialState, status: "UN_AUTHENTICATED" });
    }

    // Note: we don't want any deps triggering this useEffect. We only want a
    // re-render of the AuthProvider to trigger this (such as a browser refresh)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * GET A NEW FIREBASE AUTH ID TOKEN
   *  - check if user exists and is accessible in Firebase
   *  - attempt to refresh the user's `idToken`
   */
  const getFirebaseIdToken = useCallback(async () => {
    // Note: we could use `.catch`, which provides the following error: "No User is signed in."
    // await FirebaseAuthentication.getIdToken().catch((err) => console.error("getIdToken error: ", err));
    // However, we're instead checking for existence of user first before calling `.getIdToken`
    // so we don't need the `.catch`
    const currentUser = await getFirebaseCurrentUser();
    if (!currentUser) {
      return handleRefreshFailure();
    }

    // Note: it's recommended to use the `getIdToken` method as that is manged by Firebase
    // the raw token is the initial one managed by Google and requires us to handle refreshing
    // of the token manually. It's also advised to not use the `forceRefresh` option as this
    // returns a brand new `idToken` each time, which is not needed when using the `getIdToken`
    // method as this will attempt to return a new idToken when it has expired
    // const result = await FirebaseAuthentication.getIdToken({ forceRefresh: true });

    // Note: returns a Firebase token (via `getIdToken`) that is different from the raw Google token (`credential.idToken`)
    const result = await FirebaseAuthentication.getIdToken();
    return result.token;
  }, [handleRefreshFailure]);

  /**
   * HANDLE PROVIDER SIGN IN
   *  - attempt to sign user in with Google or Cognito
   *  - attempt to retrieve data of authenticated user from Swing
   */
  const onSignIn = useCallback(
    async (user: User | CognitoUserSession | LegacyAuthData, provider: AuthServiceProvider) => {
      const authExtras: {
        provider?: string;
        refreshTokenExpiration?: number;
        authentication?: { idToken: string };
      } = {};

      // manually add expiration of refreshToken for Cognito
      if (provider === "cognito") {
        // Note: THIS MUST ALIGN WITH AWS COGNITO POOL
        // It doesn't seem possible to retrieve expiration dynamically via API decode from token itself.
        // The default for a `refreshToken` is 30 days so we are setting this to 29 days which will force
        // the user to sign back in after this time. Each time a user signs out and signs back into the
        // app they will get a new refresh token (along with a new idToken and accessToken)
        // Note: 1000 milliseconds * 60 seconds * 60 minutes * 24 hours * 29 days
        const COGNITO_REFRESH_TOKEN_VALIDITY_TIMESTAMP = new Date(
          Date.now() + 1000 * 60 * 60 * 24 * 29,
        ).getTime();
        authExtras["refreshTokenExpiration"] = COGNITO_REFRESH_TOKEN_VALIDITY_TIMESTAMP;
      }

      // get Firebase user auth idToken and set value for `authentication.idToken` so AuthModel can access
      // Note: `idToken` is not part of the initial payload from Firebase auth
      if (provider === "google") {
        const firebaseAuthIdToken = await getFirebaseIdToken();
        if (!firebaseAuthIdToken) {
          return handleRefreshFailure();
        }
        authExtras.authentication = { idToken: firebaseAuthIdToken };
      }

      // add provider data
      authExtras["provider"] = provider;
      // set data in storage
      storage.setItem(storageAuthKey, { ...user, ...authExtras });
      // set in-memory instance to use in app
      const authData = new AuthModel({ ...user, ...authExtras });
      setState((prevState) => ({ ...prevState, authInfo: authData, apiError: undefined }));
      // set axios header
      api.defaults.headers.common.Authorization = `Token ${authData.token}`;
      if (vgsApi) {
        vgsApi.defaults.headers.common.Authorization = `Token ${authData.token}`;
      }

      /**
       * get user data
       *
       * Note: it is important that we `await` this function in order to avoid issues
       * with Safari when attempting to redirect the user
       */
      const userInfo: AuthContextState<any>["userInfo"] = await fetchUser();

      // set auth cookie for non-aliased users
      if (userInfo && !userInfo.isAlias) {
        Cookies.set(appAuthCookieName, `${authData.token}`, authCookieOptions);
      }

      // for a non-aliased google user that signs in, we need to sync data with Swing system once token is set
      if (provider === "google") {
        if (!userInfo.isAlias) {
          // note: if syncGoogleUserData fails then auth token is removed and user re-directed to login
          await syncGoogleUserData();
        }
      }
    },
    [
      api.defaults.headers.common,
      appAuthCookieName,
      authCookieOptions,
      fetchUser,
      getFirebaseIdToken,
      handleRefreshFailure,
      storage,
      storageAuthKey,
      syncGoogleUserData,
      vgsApi,
    ],
  );

  /**
   * HANDLE FIREBASE (GOOGLE) REFRESH TOKEN
   *  - attempt to update `idToken` with a new one
   *  - set localStorage and app data with updated token
   */
  const refreshGoogle = useCallback(
    async (failedRequest: AxiosError) => {
      try {
        const firebaseAuthIdToken = await getFirebaseIdToken();
        if (!firebaseAuthIdToken) {
          throw new Error("Retrieving token via firebaseAuthIdToken failed");
        }
        if (!failedRequest.response) {
          throw new Error("failedRequest response is missing so unable to assign token");
        }
        // update headers on this request in-flight request for retry
        failedRequest.response.config.headers.Authorization = `Token ${firebaseAuthIdToken}`;
        // update headers on instance
        api.defaults.headers.common.Authorization = `Token ${firebaseAuthIdToken}`;
        // update storage
        const storageData = storage.getItem(storageAuthKey);
        storageData.authentication.idToken = firebaseAuthIdToken;
        storage.setItem(storageAuthKey, storageData);
        // then update our in-app memory state so this stays in sync with storage source of truth
        setState((prevState) => ({ ...prevState, authInfo: new AuthModel(storageData) }));
        // return promise fulfilling to be the token value
        return Promise.resolve();
      } catch (err: unknown) {
        // user can be signed into app but decide to sign out of their Google account
        // and we only want to log this scenario as info to capture occurrences
        const report =
          (err as { type: string }).type === "userLoggedOut" ? console.info : console.error;
        report("Unable to get new tokens for Google Auth: ", getErrorMessage(err));
        // unable to get new tokens so we need to sign user out
        return handleRefreshFailure();
      }
    },
    [
      api.defaults.headers.common,
      getFirebaseIdToken,
      handleRefreshFailure,
      storage,
      storageAuthKey,
    ],
  );

  /**
   * HANDLE COGNITO REFRESH TOKEN
   *  - attempt to retrieve a new `idToken`
   *  - set localStorage and app data with updated token
   */
  const refreshCognito = useCallback(
    async (failedRequest: AxiosError) => {
      const user = userPool.getCurrentUser();
      if (!user) {
        console.error("No Cognito user found");
        return handleRefreshFailure();
      }

      return user?.getSession((err: Error | null, userSession: CognitoUserSession) => {
        if (err) {
          // unable to retrieve user so we need to sign user out
          console.error("getSession - error: ", err);
          return handleRefreshFailure();
        }

        const refreshToken = userSession.getRefreshToken();
        return user.refreshSession(refreshToken, (err, session) => {
          if (err) {
            // unable to get tokens so we need to sign user out
            console.error("refreshSession - error: ", err);
            return handleRefreshFailure();
          }
          /**
           * {
           *  accessToken: {
           *    jwtToken: "JWT string",
           *    payload: { ... }
           *  },
           *  clockDrift: 0,
           *  idToken: {
           *    jwtToken: "JWT string",
           *    payload: { ... }
           *  },
           *  refreshToken: {
           *    token: "non-JWT string"
           *  }
           * }
           */
          // Note: an internal library method "cacheTokens" in "CognitoUser.js" saves
          // the tokens to localStorage using the hardcoded Cognito prefix for each key
          // when this method (refreshSession) is successful so we don't need to worry about that.
          // e.g. `__swingsubsdb/<cognitoKeyForIdToken>` and `__swingsubsdb/<cognitoKeyForAccessToken>`
          // is automatically updated. However, we do need to handle the update of the existing
          // "__swingsubsdb/auth" stored info so that these new token payloads are set there correctly.
          // Note: internally 'AuthFlow: REFRESH_TOKEN_AUTH' is used
          // https://docs.aws.amazon.com/cognito/latest/developerguide/amazon-cognito-user-pools-using-the-refresh-token.html
          const idJwtToken = session.idToken.jwtToken;
          if (!failedRequest.response) {
            throw new Error("failedRequest response is missing so unable to assign token");
          }
          // update headers on this request in-flight request for retry
          failedRequest.response.config.headers.Authorization = `Token ${idJwtToken}`;
          // update headers on instance
          api.defaults.headers.common.Authorization = `Token ${idJwtToken}`;
          // update storage
          let storageData = storage.getItem(storageAuthKey);
          storageData = {
            ...storageData,
            idToken: session.idToken,
            accessToken: session.accessToken,
          };
          storage.setItem(storageAuthKey, storageData);
          // then update our in-app memory state so this stays in sync with storage source of truth
          setState((prevState) => ({ ...prevState, authInfo: new AuthModel(storageData) }));
          // return promise fulfilling to be the token value
          return Promise.resolve();
        });
      });
    },
    [api.defaults.headers.common, handleRefreshFailure, storage, storageAuthKey, userPool],
  );

  /**
   * REFRESH ID TOKEN MIDDLEWARE
   *  - determine provider to pass refresh token attempt to
   */
  const refreshTokens = useCallback(
    async (failedRequest: AxiosError, provider: AuthServiceProvider) => {
      switch (provider) {
        case "google":
          return await refreshGoogle(failedRequest);
        case "cognito":
          return await refreshCognito(failedRequest);
        default:
          console.error(`provider ${provider} unknown - unable to refresh tokens`);
          return Promise.reject();
      }
    },
    [refreshCognito, refreshGoogle],
  );

  /**
   * MIDDLEWARE REQUEST INTERCEPTOR
   *  - 3rd party middleware manages attempting to refresh the `idToken`
   *    based on the provider when request response status is 401 or 403
   *  - if needed, we could handle this ourselves and create our own custom
   *    middleware response handler but this approach includes some useful features
   *    see https://github.com/Flyrell/axios-auth-refresh
   */
  useEffect(() => {
    createAuthRefreshInterceptor(
      // @ts-expect-error is not assignable to parameter of type 'AxiosInstance'
      api,
      async (failedRequest: AxiosError) => {
        // Note: using 'localStorage` vs. 'state' here since there are issues with the
        // 'state' being "closed" on for initial AuthProvider 'state' values and therefore,
        // "provider" and "refreshTokenExpiration" result in both being 'undefined'
        try {
          const storageData = storage.getItem(storageAuthKey);
          if (storageData) {
            const authData = new AuthModel(storageData);
            const { provider, refreshTokenExpiration } = authData;
            // update app state for future re-renders
            setState((prevState) => ({ ...prevState, authInfo: authData }));
            // exit early if refresh token has expired
            if (isRefreshTokenExpired(provider, refreshTokenExpiration)) {
              console.info(`provider ${provider} refresh token expired`);
              return handleRefreshFailure();
            }
            return await refreshTokens(failedRequest, provider);
          }
          // no 'localStorage' data for some reason (i.e. system or user cleared it)
          throw new Error("localStorage data not available");
        } catch (err) {
          console.error("createAuthRefreshInterceptor failed: ", getErrorMessage(err));
          return handleRefreshFailure();
        }
      },
      {
        // 401 - token invalid or expired, 403 - token missing
        statusCodes: [401, 403],
        pauseInstanceWhileRefreshing: true,
      },
    );
  }, [api, handleRefreshFailure, refreshTokens, storage, storageAuthKey]);

  /**
   * ACTIONS - Sign In
   */
  const signIn = useCallback(
    async (props: SignInActionProps) => {
      const { provider } = props;
      const user = "user" in props ? props.user : undefined;

      try {
        // assume we are not authenticated and indicate user is attempting to authenticate
        setState((prevState) => ({ ...prevState, isAuthenticating: true }));
        // attempt to login with provider
        switch (provider) {
          case "google":
            {
              const googleUser = await onGoogleSignIn({
                scopes: ["openid", "profile", "email"],
                mode: "popup",
              });
              // googleUser can be undefined if user closes pop up without signing in
              if (googleUser) {
                await onSignIn(googleUser, provider);
              }
            }
            break;
          case "cognito":
            {
              if (!user) {
                throw new Error("user must be provided when Cognito login attempted!");
              }
              const cognitoUser = await onCognitoSignIn(user);
              await onSignIn(cognitoUser, provider);
            }
            break;
          default:
            throw new Error(`${provider} is not a supported provider.`);
        }
      } catch (err) {
        throw new Error(getErrorMessage(err));
      } finally {
        // update since the action has completed
        setState((prevState) => ({ ...prevState, isAuthenticating: false }));
      }
    },
    [onCognitoSignIn, onGoogleSignIn, onSignIn],
  );

  /**
   * ACTIONS - Sign Out
   */
  const signOut = useCallback(
    async (params?: SignOutActionParams) => {
      try {
        // attempt to sign out via provider
        switch (state.authInfo?.provider) {
          case "google":
            await onGoogleSignOut();
            break;
          case "cognito":
            await onCognitoSignOut();
            break;
          default:
            throw new Error(`${state.authInfo?.provider} is not a supported provider.`);
        }
      } catch (err) {
        throw new Error(getErrorMessage(err));
      } finally {
        onSignOut(params);
      }
    },
    [onCognitoSignOut, onGoogleSignOut, onSignOut, state.authInfo?.provider],
  );

  /**
   * ACTIONS - Refresh User
   *  - retrieve the latest user data
   */
  const refreshUser = useCallback(() => fetchUser(), [fetchUser]);

  /**
   * CONTEXT PROVIDER
   */
  const ContextProvider = useMemo(
    () => (
      <AuthContext.Provider
        value={{
          ...state,
          signIn,
          signOut,
          refreshUser,
          forgotPasswordRequest,
          forgotPasswordSubmit,
        }}
      >
        {children}
      </AuthContext.Provider>
    ),
    [state, signIn, signOut, refreshUser, forgotPasswordRequest, forgotPasswordSubmit, children],
  );

  return ContextProvider;
}

/**
 * CONTEXT HOOK
 */
export function useAuth<TUser>() {
  const authContext = useContext<AuthContextValue<TUser> | null>(AuthContext);

  if (authContext === null) throw new Error("useAuth must be used within an AuthProvider!");

  return authContext;
}

/**
 * HELPER FUNCTION
 */
function isRefreshTokenExpired(provider: AuthServiceProvider, expiration?: number) {
  if (provider === "cognito") {
    // check whether refresh token has expired by comparing
    // today's timestamp with refresh token expiration
    if (expiration && Date.now() >= expiration) {
      return true;
    }
    return false;
  }
  return false;
}
